/**
 * @file Pure view helpers for the Courses panel — status derivation,
 *       rail filtering, and rail counts. No React, no DOM; unit-tested
 *       directly.
 * @module @labcraft/admin/ui/courses/view
 */

import type { InstalledCourse } from '../../shared/courses_routes.ts'

/**
 * A course's operator status. `installed_courses` has no "draft" state
 * (a row is either enabled or not), so the redesign's Draft rail filter
 * is omitted — the honest vocabulary is enabled vs disabled.
 */
export type CourseStatus = 'enabled' | 'disabled'

/** The rail filter selection. */
export type CourseFilter = 'all' | CourseStatus

/** Pure derivations over the installed-course list. */
export class CourseView {
  /**
   * A course is enabled iff it carries an `enabledAt` stamp.
   *
   * @param course - The installed-course row.
   * @returns Its operator status.
   */
  static status(course: InstalledCourse): CourseStatus {
    return course.enabledAt !== null ? 'enabled' : 'disabled'
  }

  /**
   * Filter the catalog to the rail's current selection.
   *
   * @param courses - All installed courses.
   * @param filter - The active rail filter.
   * @returns The matching rows (order preserved).
   */
  static filter(
    courses: InstalledCourse[],
    filter: CourseFilter,
  ): InstalledCourse[] {
    if (filter === 'all') return courses
    return courses.filter(c => CourseView.status(c) === filter)
  }

  /**
   * Rail counts per filter bucket.
   *
   * @param courses - All installed courses.
   * @returns `{ all, enabled, disabled }`.
   */
  static counts(courses: InstalledCourse[]): {
    all: number
    enabled: number
    disabled: number
  } {
    const enabled = courses.filter(c => c.enabledAt !== null).length
    return { all: courses.length, enabled, disabled: courses.length - enabled }
  }
}
